import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useWindowScroll, useIntersection } from 'react-use';

import ErrorBoundary from './components/ErrorBoundary';
import Placeholder from './components/Placeholder';
import Container from './components/templates/Container';
import { useOptions } from './hooks/useOptions';
import { usePosts } from './hooks/usePosts';
import { LbStatus } from './types';

const fakePosts = Array.from({ length: 4 }, (_, i) => ({ id: i }));

const LbContainter = () => {
  const {
    blogId,
    cssClass,
    autostart,
    loadOnScroll,
    scrollThreshold,
    hideNewPosts,
    showNewPostsOnScroll,
    maxPosts,
    useSkeletonPosts,
    useLoadingSkeletonPosts,
    customComponents: {
      container: ContainerComponent = Container,
      placeholder: PlaceholderComponent = Placeholder,
      emptyMessage: EmptyMessageComponent,
    },
  } = useOptions();
  const scrollSpyRef = useRef(null);
  const observed = useIntersection(scrollSpyRef, {
    rootMargin: `0px 0px ${scrollThreshold ?? '0px'} 0px`,
  });
  const { posts, showAddedPosts, loadPosts, startPolling, stopPolling, status } = usePosts();
  const loading = useMemo(() => ([LbStatus.LOADING, LbStatus.LOADING_MORE] as LbStatus[]).includes(status), [status]);
  const empty = useMemo(() => status === LbStatus.EMPTY, [status]);
  const emptyOrFinished = useMemo(() => ([LbStatus.EMPTY, LbStatus.FINISHED] as LbStatus[]).includes(status), [status]);

  useEffect(() => {
    if (autostart) {
      loadPosts().then(() => {
        startPolling();
      });
    }

    return () => {
      stopPolling();
    };
  }, [loadPosts, autostart, startPolling, stopPolling]);

  useEffect(() => {
    let interval: NodeJS.Timeout = null;
    if (!loadOnScroll || !observed?.isIntersecting || emptyOrFinished) {
      clearInterval(interval);
      return;
    }

    loadPosts();
    interval = setInterval(() => {
      loadPosts();
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [observed?.isIntersecting, loadOnScroll, loadPosts, emptyOrFinished]);

  const { y } = useWindowScroll();
  useEffect(() => {
    if (!y || !hideNewPosts || !showNewPostsOnScroll) return;
    showAddedPosts();
  }, [hideNewPosts, showAddedPosts, showNewPostsOnScroll, y]);

  const visiblePosts = useMemo(() => (maxPosts && posts ? posts.slice(0, maxPosts) : posts), [maxPosts, posts]);

  if (!blogId) return null;

  return (
    <div id={`lb-${blogId}`} className={cssClass}>
      {(!visiblePosts.length || empty) && (
        <Suspense>
          {EmptyMessageComponent && <EmptyMessageComponent />}
          {useSkeletonPosts && fakePosts.map(({ id }) => <PlaceholderComponent key={`placeholder_${id}`} />)}
        </Suspense>
      )}
      {!!visiblePosts.length &&
        visiblePosts.map((post, i) => (
          <Suspense key={`post_${post.id ?? i}`} fallback={<PlaceholderComponent />}>
            <ErrorBoundary data={post}>
              <ContainerComponent post={post} />
            </ErrorBoundary>
          </Suspense>
        ))}
      {useLoadingSkeletonPosts && loading && (
        <Suspense>
          <PlaceholderComponent />
        </Suspense>
      )}
      <div ref={scrollSpyRef} />
    </div>
  );
};

export default LbContainter;
